import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { BrandButton } from "@/components/PageHeader";
import { Field, Pick } from "@/components/event-setup/BasicsSection";
import { PasswordRules, passwordOk } from "@/components/auth/PasswordRules";
import { ROLES, defaultModules } from "@/lib/access";
import { api, errMsg } from "@/lib/api";
import { ModulePicker } from "./ModulePicker";

const EMPTY = { name: "", email: "", username: "", role: "Registration Officer", password: "", active: true, send_email: true, can_export: true, modules: defaultModules("Registration Officer") };

const EmailToggle = ({ f, set }) => (
  <label className="flex items-center gap-2 text-sm"><Switch checked={f.send_email} onCheckedChange={(v) => set({ send_email: v })} data-testid="staff-send-email-toggle" />Email the username and default password to this person</label>
);

export const StaffDialog = ({ open, staff, onClose, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const txt = (k) => (e) => set({ [k]: e.target.value });
  useEffect(() => {
    if (open) { setF(staff ? { ...EMPTY, ...staff, modules: staff.modules || defaultModules(staff.role) } : EMPTY); }
  }, [open, staff]);
  const save = async () => {
    if (!staff && !passwordOk(f.password)) return toast.error("The default password does not meet the rules");
    if (f.role !== "Super Admin" && !f.modules.length) return toast.error("Choose at least one module this person can open");
    setBusy(true);
    try {
      if (staff) await api.put(`/staff/${staff.id}`, { name: f.name, email: f.email, role: f.role, active: f.active, modules: f.modules, can_export: f.can_export });
      else await api.post("/staff", { name: f.name, email: f.email, username: f.username, role: f.role, password: f.password, send_email: f.send_email, modules: f.modules, can_export: f.can_export });
      toast.success(staff ? "Staff details updated" : `${f.name} added${f.send_email ? " and emailed" : ""}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="staff-dialog">
        <DialogHeader><DialogTitle className="font-heading">{staff ? "Edit staff member" : "Add staff member"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Full name"><Input value={f.name} onChange={txt("name")} data-testid="staff-name-input" /></Field>
          <Field label="Email address"><Input type="email" value={f.email} onChange={txt("email")} data-testid="staff-email-input" /></Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Username"><Input value={f.username} onChange={(e) => set({ username: e.target.value.toLowerCase().replace(/\s/g, "") })} disabled={!!staff} className="font-mono" data-testid="staff-username-input" /></Field>
            <Field label="Role (title)"><Pick value={f.role} onChange={(v) => set(staff && v !== "Super Admin" && f.role !== "Super Admin" ? { role: v } : { role: v, modules: defaultModules(v) })} options={ROLES} testId="staff-role-select" /></Field>
          </div>
          <Field label="Modules this person can open"><ModulePicker role={f.role} value={f.modules} onChange={(modules) => set({ modules })} /></Field>
          {f.role !== "Super Admin" && <label className="flex items-center gap-2 text-sm rounded-md border p-3" data-testid="staff-exports-row"><Switch checked={f.can_export !== false} onCheckedChange={(v) => set({ can_export: v })} data-testid="staff-exports-toggle" /><span><span className="font-semibold">Exports {f.can_export !== false ? "on" : "off"}</span><span className="block text-xs text-gray-500">When off, this person sees no Export or Download CSV buttons on any page they can open.</span></span></label>}
          {!staff && (
            <div>
              <Field label="Default password"><Input value={f.password} onChange={txt("password")} className="font-mono" autoComplete="off" data-testid="staff-password-input" /></Field>
              <PasswordRules value={f.password} />
              <p className="text-xs text-gray-500 mt-2">They must set a permanent password the first time they sign in.</p>
            </div>
          )}
          {!staff && <EmailToggle f={f} set={set} />}
          {staff && <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => set({ active: v })} data-testid="staff-active-toggle" />Account active</label>}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <BrandButton variant="outline" onClick={onClose} data-testid="staff-cancel-button">Cancel</BrandButton>
          <BrandButton onClick={save} disabled={busy} data-testid="staff-save-button">{busy ? "Saving…" : staff ? "Save changes" : "Add staff"}</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ResetDialog = ({ staff, onClose, onSaved }) => {
  const [f, setF] = useState({ password: "", send_email: true });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const save = async () => {
    if (!passwordOk(f.password)) return toast.error("The default password does not meet the rules");
    setBusy(true);
    try {
      await api.post(`/staff/${staff.id}/reset-password`, f);
      toast.success(`Password reset for ${staff.name}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <Dialog open={!!staff} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="staff-reset-dialog">
        <DialogHeader><DialogTitle className="font-heading">Reset password for {staff?.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-500 -mt-2">Set a new default password. They will be signed out everywhere and must choose a permanent password at next sign-in.</p>
        <div>
          <Field label="New default password"><Input value={f.password} onChange={(e) => set({ password: e.target.value })} className="font-mono" autoComplete="off" data-testid="staff-reset-password-input" /></Field>
          <PasswordRules value={f.password} />
        </div>
        <EmailToggle f={f} set={set} />
        <div className="flex justify-end gap-2">
          <BrandButton variant="outline" onClick={onClose}>Cancel</BrandButton>
          <BrandButton onClick={save} disabled={busy} data-testid="staff-reset-save-button">{busy ? "Saving…" : "Reset password"}</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
